import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Sparkles } from "lucide-react";
import { STORE } from "../../data/site";
import { AppleMark, PlayStoreMark } from "./BrandMarks";

const TRIAL_URL = "https://pay.rev.cat/kaooarqpwiwxiwlj";
const ease = [0.22, 1, 0.36, 1];

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden py-[0.08em]">
    <motion.span
      className="block leading-[1.02]"
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, ease, delay }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.05]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      ref={ref}
      className="relative flex min-h-screen items-center overflow-hidden pt-28 pb-16"
      data-testid="hero-section"
    >
      <motion.div
        style={{ y: imgY, scale: imgScale }}
        className="pointer-events-none absolute right-4 top-0 hidden h-full w-[42%] items-center justify-center xl:right-16 lg:flex"
      >
        <div className="relative aspect-[9/19.5] h-[78%] max-h-[660px] overflow-hidden rounded-[2.75rem] border border-white/12 bg-black p-2 shadow-[0_50px_120px_-25px_rgba(124,58,237,0.6)]">
          <img
            src="/app-shots/IMG_4494.webp"
            alt="Inside the Radiate app"
            className="h-full w-full rounded-[2.3rem] object-cover object-top"
          />
        </div>
      </motion.div>

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 mx-auto w-full max-w-7xl px-6 md:px-10"
      >
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.15 }}
          className="mb-8 text-xs font-semibold uppercase tracking-[0.35em] text-white/50"
        >
          Regulate · Rewire · Radiate
        </motion.p>

        <h1 className="max-w-3xl font-display text-4xl font-thin leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl">
          <Line delay={0.32}>From overthinking</Line>
          <Line delay={0.42}>
            <span className="italic text-white/85">
              to <span className="font-bold text-white">embodied</span>.
            </span>
          </Line>
          <Line delay={0.52}>From performing</Line>
          <Line delay={0.62}>
            <span className="italic text-white/85">
              to <span className="font-bold text-white">magnetic</span>.
            </span>
          </Line>
          <Line delay={0.72}>
            <span className="italic text-white/85">Ten minutes a day.</span>
          </Line>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease, delay: 1.0 }}
          className="mt-9 max-w-lg text-base leading-relaxed text-white/60 md:text-lg"
        >
          Daily audio that regulates your nervous system first, rewires the
          stories keeping you stuck, then helps you manifest the version of your
          life you've been dreaming of.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease, delay: 1.15 }}
          className="mt-11 flex flex-col gap-4 sm:flex-row sm:items-center"
        >
          <a
            href={TRIAL_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center justify-center gap-3 rounded-full bg-white px-8 py-4 text-sm font-medium text-black transition-transform duration-300 hover:scale-105 hover:shadow-[0_0_30px_rgba(255,255,255,0.35)]"
            data-testid="hero-trial-btn"
          >
            <span className="grid h-7 w-7 place-items-center rounded-full bg-black text-white transition-transform duration-300 group-hover:scale-110">
              <Sparkles className="h-3.5 w-3.5" />
            </span>
            Try Radiate one week free
          </a>
          <div className="flex items-center gap-3">
            <a
              href={STORE.appStore}
              target="_blank"
              rel="noopener noreferrer"
              className="grid h-[52px] w-[52px] place-items-center rounded-full border border-white/15 bg-white/5 text-white backdrop-blur-xl transition-colors duration-300 hover:bg-white/10"
              data-testid="hero-app-store"
              aria-label="Download on the App Store"
            >
              <AppleMark className="h-5 w-5" />
            </a>
            <a
              href={STORE.googlePlay}
              target="_blank"
              rel="noopener noreferrer"
              className="grid h-[52px] w-[52px] place-items-center rounded-full border border-white/15 bg-white/5 backdrop-blur-xl transition-colors duration-300 hover:bg-white/10"
              data-testid="hero-google-play"
              aria-label="Get it on Google Play"
            >
              <PlayStoreMark className="h-5 w-5" />
            </a>
          </div>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 1 }}
          className="mt-6 text-xs uppercase tracking-[0.2em] text-white/35"
        >
          One week on us · No experience needed
        </motion.p>
      </motion.div>
    </section>
  );
};
